<template>
   <!-- Vuetify Base app component. -->
   <v-app>
      <!-- Impersonation banner, above every layout -->
      <ImpersonationBanner></ImpersonationBanner>

      <!-- Area layouts are route parents (MainLayout, SettingsLayout, ...) -->
      <router-view></router-view>

      <!-- Global overlays, outside the layout chrome -->
      <ErrorModal
         v-model:show="showError"
         v-model:error-message="errorMessage"
         :multiline-errors="secondaryErrors"
      ></ErrorModal>
      <DialogHolder></DialogHolder>
      <SnackbarHolder></SnackbarHolder>
   </v-app>
</template>

<script lang="ts">
import ErrorModal from '@c/layout/ErrorModal.vue';
import ImpersonationBanner from '@c/layout/ImpersonationBanner.vue';
import DialogHolder from '@src/plugins/dialog_service/DialogHolder.vue';
import SnackbarHolder from '@src/plugins/dialog_service/SnackbarHolder.vue';
import {buildAppVuetifyDefaults} from '@s/app_vuetify_theme';
import {vuetify} from '@src/plugins/vuetify';
import {startSessionScopedStores} from '@src/store/session_scope';
import {useUiStore} from '@src/store/ui_store';
import type {AxiosError} from 'axios';
import axios from 'axios';
import {defineComponent} from 'vue';
import {captureAnalyticsException} from './plugins/analytics';

interface ICompData {
   showError: boolean;
   errorMessage: string;
   secondaryErrors: string[];
}

interface IBasicAxiosError {
   message: string;
}

/**
 * Root component. Starts the session-scoped stores, provides `onErrorBase` (used through
 * ErrorMixin) and mounts the dialog, snackbar and error-modal holders.
 */
export default defineComponent({
   components: {
      ErrorModal,
      ImpersonationBanner,
      DialogHolder,
      SnackbarHolder,
   },

   provide(): Record<string, any> {
      return {
         onErrorBase: (err: Error, secondaryErrors: string[] = []) => {
            let errorMessage = err.message;
            if (axios.isAxiosError(err)) {
               const axiosError = err as AxiosError<IBasicAxiosError>;
               console.error(axiosError.response?.data);
               errorMessage = axiosError.response?.data.message ?? err.message;

               captureAnalyticsException(err);
            } else {
               console.error(err);
               captureAnalyticsException(err);
            }

            this.showError = true;
            this.errorMessage = errorMessage;
            this.secondaryErrors = secondaryErrors;
         },
      };
   },

   data(): ICompData {
      return {
         showError: false,
         errorMessage: '',
         secondaryErrors: [],
      };
   },

   async created(): Promise<void> {
      await startSessionScopedStores();
   },

   mounted(): void {
      this.applyUiPreferences();

      const ui_store = useUiStore();
      this.$watch(
         () => [ui_store.themePreference, ui_store.densityPreference],
         () => this.applyUiPreferences(),
      );
   },

   methods: {
      /**
       * Apply the stored UI preferences: the Vuetify theme, the density-built Vuetify
       * defaults, and the data-density attribute the density CSS tokens key off.
       */
      applyUiPreferences(): void {
         const ui_store = useUiStore();
         void this.$vuetify.theme.change(ui_store.themePreference);
         vuetify.defaults.value = buildAppVuetifyDefaults(ui_store.densityPreference);
         document.documentElement.setAttribute('data-density', ui_store.densityPreference);
      },
   },
});
</script>

<style lang="scss" scoped></style>
